import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    DEFAULT_PAGE_SIZE,
    fetch as fetchOne,
    fetchRecords,
    update,
} from '../services/DomainNamesService';

import {DomainName} from '../types';

// FNP-942: records are React Query's job. Selection, paging and keyword stay in
// Redux — see useDomainNamesSelection.ts and the visibility slice.
//
// The service layer is unchanged: these hooks call the same DomainNamesService
// functions the Redux thunks used to call, so the GraphQL surface and the
// backend's user-isolation filtering behave exactly as before.

export const domainNamesKeys = {
    all: ['domain-names'] as const,
    lists: () => [...domainNamesKeys.all, 'list'] as const,
    list: (filter: object) => [...domainNamesKeys.lists(), filter] as const,
    details: () => [...domainNamesKeys.all, 'detail'] as const,
    detail: (id: string) => [...domainNamesKeys.details(), id] as const,
};

// Page size is a SERVER concern under token pagination — fixed by the module,
// never a "rows per page" dropdown.
export const PAGE_SIZE = DEFAULT_PAGE_SIZE;

export const useDomainNamesQuery = (filter: object = {}) => {
    return useInfiniteQuery({
        queryKey: domainNamesKeys.list(filter),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords(filter, PAGE_SIZE, pageParam),
        // ⚠️ THE ONLY END-OF-RESULTS SIGNAL IS A NULL nextToken, and this MUST
        // return `undefined` when done. Returning null — or repeating the same
        // token — makes React Query refetch forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) {
                return undefined;
            }
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        staleTime: 60_000,
    });
};

export const useDomainNameQuery = (domainNameId?: string) => {
    return useQuery({
        queryKey: domainNamesKeys.detail(domainNameId ?? ''),
        queryFn: async () => {
            if (typeof domainNameId === 'undefined') {
                throw new Error('domainNameId is required parameter');
            }
            return await fetchOne(domainNameId);
        },
        // Without this the detail query fires with an empty id on the Add page,
        // where there is no record yet.
        enabled: typeof domainNameId !== 'undefined' && domainNameId !== '',
        staleTime: 60_000,
    });
};

export const useCreateDomainNameMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        // FNP-1282: no cast and no spread — the service builds the payload by
        // whitelist, so the create path is no longer unfiltered either.
        mutationFn: (values: DomainName) => add(values),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: domainNamesKeys.lists()});
        },
    });
};

export const useUpdateDomainNameMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
                         id,
                         record
                     }: { id: string, record: DomainName }) => update(id, record),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: domainNamesKeys.lists()});
            queryClient.invalidateQueries({queryKey: domainNamesKeys.detail(variables.id)});
        },
    });
};

export const useDeleteDomainNameMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id),
        onSuccess: (_data, id) => {
            queryClient.invalidateQueries({queryKey: domainNamesKeys.lists()});
            // FNP-1282: the detail page is a reachable caller, so leaving the
            // deleted record's detail cache warm serves a record that is gone.
            queryClient.invalidateQueries({queryKey: domainNamesKeys.detail(id)});
        },
    });
};
